import * as React from 'react'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import { useTheme } from '@mui/material/styles'
import { useCalendarNavigation } from './hooks/useCalendarNavigation'
import { useCalendarViewPreference } from './hooks/useCalendarViewPreference'
import { useRefreshOverdueCount } from './hooks/useRefreshOverdueCount'
import { useCalendarEvents } from './hooks/useCalendarEvents'
import { useCalendarFilterPreferences } from './hooks/useCalendarFilterPreferences'
import { useActivityTypeFilter } from './hooks/useActivityTypeFilter'
import { DEFAULT_CALENDAR_VIEW } from './viewPreference'
import { CalendarToolbar } from './components/CalendarToolbar/CalendarToolbar'
import { CalendarGrid } from './components/CalendarGrid/CalendarGrid'
import { CreateEventModal } from '@user/features/Calendar/components/CreateEventModal'
import { ConfirmationDialog } from '@sales-center/components'
import { useDialogState } from '@user/utils/useDialogState'
import { useOnTabRefocus } from '@user/hooks/useOnTabRefocus'
import { useCalendarActivityActions } from './hooks/useCalendarActivityActions'
import {
  CalendarEmptyState,
  type CalendarEmptyVariant,
} from './components/CalendarEmptyState'
import { EventDetailPopover } from './components/EventDetailPopover'
import { CalendarSelectionProvider } from './components/EventDetailPopover/selectionContext'
import {
  CalendarCreateProvider,
  type CreateEventPrefill,
} from './components/createEventContext'
import type { CalendarSelection } from './components/EventDetailPopover/selectionContext'
import { dayHeading, monthHeading, weekHeading } from './dateFormat'
import type { CalendarEvent, CalendarView } from './typings'
import type { ActivityStatus } from '@user/features/Activity/constants'

// Server-derived per user (CONTRACT-BOUNDARY.md §5). The merged SH-1408 feed has
// no Google provider or connection state yet, so the chip stays hidden rather
// than offering a filter that can never match.
const GOOGLE_CONNECTED = false

function headingFor(
  view: CalendarView,
  cursor: Date,
  range: { start: Date; endInclusive: Date },
): string {
  switch (view) {
    case 'month':
      return monthHeading(cursor)
    case 'day':
      return dayHeading(cursor)
    case 'week':
    default:
      return weekHeading(range.start, range.endInclusive)
  }
}

/**
 * Route shell for the Unified Calendar. Owns view / cursor / filter state and
 * mounts inside the app's existing PageScroll (the single scroll owner) — it
 * does NOT build AppShell / NavRail / TopBar.
 */
export function CalendarPage() {
  const theme = useTheme()
  const { preferredView, persistView } = useCalendarViewPreference()
  const refreshOverdueCount = useRefreshOverdueCount()
  const { view, cursor, range, setView, goToday, goPrev, goNext, goToDay } =
    useCalendarNavigation(preferredView ?? DEFAULT_CALENDAR_VIEW)

  // Restore the saved view (SH-1409 AC3) once it resolves. Preferences load
  // app-wide, so on a fresh navigation they may already be present — seeded via
  // the initial value above — or arrive a tick later, in which case seed exactly
  // once and then let the user own the view, so rapid toggling never snaps back
  // to an intermediate saved value (EC-4). Same late-arrival guard Collision
  // toggle uses (SH-833). An explicit selection also seeds (see handleViewChange)
  // so a save resolving after the user has already toggled can't re-fire this.
  const seededRef = React.useRef(preferredView !== undefined)
  React.useEffect(() => {
    if (seededRef.current || preferredView === undefined) return
    seededRef.current = true
    setView(preferredView)
  }, [preferredView, setView])

  // Every selection is optimistic (updates the grid now) and persisted server-
  // side (remembered across sessions/devices, AC3). A failed save never reverts
  // the view (EC-3, handled in useCalendarViewPreference). Mark the preference
  // seeded first: once the user has actively chosen, their choice owns the view,
  // so a first save that resolves a tick later (flipping `preferredView` from
  // undefined to an intermediate value) can never snap the UI back via the
  // restoration effect above.
  const handleViewChange = React.useCallback(
    (next: CalendarView) => {
      seededRef.current = true
      setView(next)
      persistView(next)
    },
    [setView, persistView],
  )

  // Drilling into Day from a "+N more" affordance is a view change like any
  // other, so it must persist too (AC3). It can't rely on the toggle to save
  // later: re-clicking the already-active Day button is a no-op (ViewToggle
  // drops MUI's null re-select), so this is the only path that records it.
  const handleShowDay = React.useCallback(
    (date: Date) => {
      seededRef.current = true
      goToDay(date)
      persistView('day')
    },
    [goToDay, persistView],
  )
  const { events, sourceStatus, isLoading, error, retry, refresh } =
    useCalendarEvents({ start: range.start, end: range.end })
  const { filters, toggle, visible } = useCalendarFilterPreferences()
  const activityTypeFilter = useActivityTypeFilter()

  // Selection drives the detail popover. `closing` keeps the popover mounted
  // through its exit transition (so it can restore focus to the tile) while
  // reporting `open=false`.
  const [selection, setSelection] = React.useState<CalendarSelection | null>(
    null,
  )
  const [selectionOpen, setSelectionOpen] = React.useState(false)

  // A refocus while the popover is open would swap data under the reader, so it
  // is deferred and run once, when the popover closes.
  const refreshPendingRef = React.useRef(false)
  const onRefocus = React.useCallback(() => {
    if (selectionOpen) refreshPendingRef.current = true
    else refresh()
  }, [selectionOpen, refresh])
  useOnTabRefocus(onRefocus, { includeWindowFocus: true })
  React.useEffect(() => {
    if (selectionOpen || !refreshPendingRef.current) return
    refreshPendingRef.current = false
    refresh()
  }, [selectionOpen, refresh])

  // A view or date change remounts the grid and detaches the tile the popover is
  // anchored to, so drop the selection outright (no tile left to restore focus to).
  const cursorTime = cursor.getTime()
  React.useEffect(() => {
    setSelection(null)
    setSelectionOpen(false)
  }, [view, cursorTime])

  // A refetch keeps the popover open on the fresh event while its tile survives;
  // once the event is gone (e.g. deleted elsewhere) or its tile remounted, close.
  const selectedId = selection?.event.id
  React.useEffect(() => {
    if (!selectedId) return
    const fresh = events.find((event) => event.id === selectedId)
    setSelection((current) => {
      if (!current || current.event.id !== selectedId) return current
      if (!fresh || !current.anchorEl.isConnected) return null
      return fresh === current.event ? current : { ...current, event: fresh }
    })
  }, [events, selectedId])

  // Whichever path cleared the selection, the popover is gone: a stuck open flag
  // would park every later refocus refresh.
  React.useEffect(() => {
    if (!selection) setSelectionOpen(false)
  }, [selection])

  const openDetail = React.useCallback(
    (event: CalendarEvent, anchorEl: HTMLElement) => {
      setSelection({ event, anchorEl })
      setSelectionOpen(true)
    },
    [],
  )

  // A status written from the popover is committed on the server, but `events`
  // still holds the value the feed returned and is not refetched on write. Keep
  // the committed value here so reopening the same event shows what was written
  // rather than the stale feed value.
  const [committedStatus, setCommittedStatus] = React.useState<
    Record<string, ActivityStatus>
  >({})

  // Drop the overrides once a fetch returns: from then on the feed is the newer
  // source, and keeping them would pin a status the server may have since
  // changed for the rest of the page's life.
  React.useEffect(() => {
    setCommittedStatus({})
  }, [events])

  // E7 — owner-only: another user's SC event comes back masked (`busy`); the
  // calendar shows only events assigned to me, so drop them before filtering.
  const ownEvents = React.useMemo(
    () => events.filter((event) => event.visibility !== 'busy'),
    [events],
  )

  const visibleEvents = React.useMemo(
    () => ownEvents.filter(visible).filter(activityTypeFilter.isVisible),
    [ownEvents, visible, activityTypeFilter.isVisible],
  )

  // SH-1428: open the create-event flow from the toolbar's "New Event" button.
  // The button/toolbar shell landed with SH-1409/SH-1963 and the create modal
  // shipped separately (PR #4991), so nothing connected the two — this backfills
  // that wiring. A successful create refetches the feed (onCreated below) so the
  // new event appears without a manual reload, now that the SH-1408 feed surfaces
  // newly created activities.
  const [isCreateOpen, openCreate, closeCreate] = useDialogState()

  // SH-1432: edit/delete an activity from the detail popover. The hook owns the
  // edit-modal and delete-confirm state; success refetches the feed (`retry`) and
  // invalidates the popover hydration cache. Opening either flow first dismisses
  // the popover so it isn't left hanging behind the dialog.
  const activityActions = useCalendarActivityActions({ retry })
  const handleEditEvent = React.useCallback(
    (event: CalendarEvent) => {
      setSelectionOpen(false)
      activityActions.onEdit(event)
    },
    [activityActions],
  )
  const handleDeleteEvent = React.useCallback(
    (event: CalendarEvent) => {
      setSelectionOpen(false)
      activityActions.onDelete(event)
    },
    [activityActions],
  )

  // SH-2133: a click on empty grid space opens the same modal, seeded with
  // the clicked slot. Held alongside the open flag rather than inside it so
  // the toolbar button keeps opening an unseeded form.
  const [createPrefill, setCreatePrefill] =
    React.useState<CreateEventPrefill | null>(null)

  const openCreateAt = React.useCallback(
    (prefill: CreateEventPrefill) => {
      // Never two layers at once: a create started from the grid dismisses
      // whatever popover was open over it.
      setSelectionOpen(false)
      setCreatePrefill(prefill)
      openCreate()
    },
    [openCreate],
  )

  // The seed is set on every open rather than cleared on close. Clearing on
  // close would change it while MUI still has the dialog mounted for its exit
  // transition, remounting the form and visibly resetting it mid-fade.
  const openCreateUnseeded = React.useCallback(() => {
    setCreatePrefill(null)
    openCreate()
  }, [openCreate])

  // The grid always renders behind any message (an empty grid is still where you
  // view and, later, create events), so an empty state is a non-blocking overlay.
  // Precedence, and never during a load (avoids a flash):
  //  - the request failed → `load-error`, never misreported as empty;
  //  - no source returned anything (AC5) → `no-events`, which wins over
  //    `filters-off` so we never claim "all types hidden" over an empty result;
  //  - events exist but the filters hide every one → `filters-off`. Keyed off
  //    what is actually visible, not chip state: unknown-source events (no chip)
  //    survive every chip being off, and a hidden chip (Google, when not
  //    connected) can never be switched off.
  // `events` holds only events inside the visible range (useCalendarEvents trims
  // the padded query), so "no events" means nothing the grid could render.
  const emptyVariant: CalendarEmptyVariant | null = isLoading
    ? null
    : error
      ? 'load-error'
      : ownEvents.length === 0
        ? 'no-events'
        : visibleEvents.length === 0
          ? 'filters-off'
          : null

  // A commit means more than the chip's own state: the overdue badge counts
  // activities app-wide, so a status written here must refresh it just as the
  // Activity panel does after its own write.
  const onStatusCommitted = React.useCallback(
    (status: ActivityStatus) => {
      const id = selection?.event.id
      if (id) setCommittedStatus((prev) => ({ ...prev, [id]: status }))
      refreshOverdueCount()
    },
    [selection?.event.id, refreshOverdueCount],
  )

  // Only the popover reads `status` today, so the override is applied here
  // rather than over the whole event list.
  const selectedEvent = React.useMemo(() => {
    if (!selection) return null
    const committed = committedStatus[selection.event.id]
    return committed
      ? { ...selection.event, status: committed }
      : selection.event
  }, [selection, committedStatus])

  const heading = headingFor(view, cursor, range)

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        boxShadow: theme.shadows[1],
      }}
    >
      <Box sx={{ padding: { xs: '14px', sm: '18px', md: '24px' } }}>
        <CalendarToolbar
          view={view}
          onViewChange={handleViewChange}
          heading={heading}
          onToday={goToday}
          onPrev={goPrev}
          onNext={goNext}
          filters={filters}
          onToggleFilter={toggle}
          googleConnected={GOOGLE_CONNECTED}
          sourceStatus={sourceStatus}
          activityTypeFilter={activityTypeFilter}
          onRetrySource={refresh}
          onNewEvent={openCreateUnseeded}
        />
        <CalendarCreateProvider open={openCreateAt}>
          <CalendarSelectionProvider open={openDetail}>
            <Box sx={{ position: 'relative' }}>
              {isLoading && (
                <LinearProgress
                  aria-label="Loading events"
                  data-test-id="calendar-loading"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                  }}
                />
              )}
              <CalendarGrid
                view={view}
                cursor={cursor}
                weekStart={range.start}
                events={visibleEvents}
                onShowDay={handleShowDay}
              />
              {emptyVariant && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <CalendarEmptyState
                    variant={emptyVariant}
                    onRetry={emptyVariant === 'load-error' ? retry : undefined}
                  />
                </Box>
              )}
            </Box>
          </CalendarSelectionProvider>
        </CalendarCreateProvider>
        <CreateEventModal
          open={isCreateOpen}
          onClose={closeCreate}
          onCreated={retry}
          prefill={createPrefill ?? undefined}
        />
        {/* SH-1432: the same dialog, opened in edit mode for the selected activity. */}
        <CreateEventModal
          open={activityActions.isEditOpen}
          editActivityId={activityActions.editActivityId}
          editEntity={activityActions.editEntity}
          onClose={activityActions.onEditClose}
          onUpdated={activityActions.onEditSuccess}
        />
        <ConfirmationDialog
          open={activityActions.isConfirmOpen}
          title="Delete Event"
          message="Are you sure you want to delete this event? This can't be undone."
          okButtonText="Delete"
          okButtonDisabled={activityActions.isDeleting}
          okClick={activityActions.onDeleteConfirm}
          cancelClick={activityActions.onDeleteCancel}
          dataTestId="delete-activity-confirm"
        />
      </Box>
      {selection && selectedEvent && (
        // Key by event so a reopen mounts a fresh popover — otherwise per-event
        // state (e.g. the status chip's seeded value) leaks between events.
        <EventDetailPopover
          key={selection.event.id}
          event={selectedEvent}
          anchorEl={selection.anchorEl}
          open={selectionOpen}
          onClose={() => setSelectionOpen(false)}
          onStatusCommitted={onStatusCommitted}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
        />
      )}
    </Box>
  )
}
